import { useEffect, useMemo, useRef, useState } from "react";
import { Rassurance } from "../components/Rassurance";
import {
  HOURS_PER_WEEK,
  PLANS,
  SECTORS,
  STAFF_BRACKETS,
  WEEKLY_HOUR_STEPS,
  QUESTION_DONNEES,
  reserveDuDernierPalier,
  classer,
  computePayrollDrain,
  echelle as echelleMaturite,
  speakingEquivalentAvecDe,
} from "@riseafrica/scoring";
import type { PayrollResult, SectorCode, StaffBracket } from "@riseafrica/scoring";
import { fetchPlanEntreprise, fetchPostesEntreprise, fetchRepereSecteur } from "../lib/api";
import { MESSAGE_EQUIPE as MESSAGE_EQUIPE_PARTAGE } from "../content/copy";
import type { EchecPlan, PlanActions as Plan, PosteEntreprise, RepereSecteur } from "../lib/api";
import { declarerPostes } from "../lib/postesDeclares";
import { defilerVers, remonter } from "../lib/defiler";
import { ActionOfferte, TitresVerrouilles } from "../components/PlanActions";
import { copyTestLink, shareWhatsApp } from "../lib/share";
import { track } from "../lib/track";
import { useApp } from "../state/AppState";
import { ApresPaiement } from "../components/ApresPaiement";
import { RappelAchat } from "../components/RappelAchat";
import { BoutonAchat } from "../components/BoutonAchat";
import { useSpecimen } from "../components/Offers";
import { RappelAgenda } from "../components/RappelAgenda";

/**
 * L'axe dirigeant — le même questionnement que pour un salarié, posé à
 * l'échelle de l'entreprise.
 *
 * Trois arbitrages du 18/08 se lisent directement dans cet écran :
 *
 * - **Le mot « diagnostic » et le mot « IA » sont écrits**, comme partout
 *   ailleurs dans le produit. Le kit d'origine les interdisait face à un
 *   dirigeant ; ce qui rend ce produit crédible est sa vérifiabilité, pas sa
 *   persuasion, et cacher le sujet reviendrait à se priver de l'argument.
 * - **La question d'argent porte sur le salaire moyen**, jamais sur le chiffre
 *   d'affaires. Elle porte sur les employés et non sur les résultats de
 *   l'entreprise, donc elle est moins intrusive — et elle supprime onze valeurs
 *   non sourcées du calcul. Elle reste facultative.
 * - **Aucun nom propre.** Ce n'est pas un autre produit, c'est un volet de
 *   STRATERA.
 */

type Etape = "profil" | "postes" | "resultat";

/** Le temps déclaré par poste, indexé par position dans la liste. */
type Heures = Record<number, number>;

const MIN_POSTES_RENSEIGNES = 2;

export function Dirigeant({ onSortir }: { onSortir: () => void }) {
  const { say, setEntreprise, prixEntreprise } = useApp();
  const [etape, setEtape] = useState<Etape>("profil");

  const [secteur, setSecteur] = useState<SectorCode | "">("");
  const [effectif, setEffectif] = useState<StaffBracket | null>(null);
  const [salaire, setSalaire] = useState("");
  /*
    L'autorisation de transmettre le coût horaire — 25/08/2026.

    Décochée par défaut, et elle le reste : la promesse écrite sous le champ
    de saisie est celle qui s'applique tant que rien n'est coché. Ce qui
    change quand elle l'est, l'écran le dit avant, pas après.
  */
  const [partageCout, setPartageCout] = useState(false);
  /*
    Les réponses de maturité — 26/08/2026.

    Posées **après** le résultat, et c'est un arbitrage du porteur : le
    dirigeant voit d'abord son montant, puis répond à cinq questions et reçoit
    son palier gratuitement. Le rapport vend le chemin, pas le palier — même
    logique que l'indice gratuit et le rapport payé sur l'axe salarié.

    Les poser avant aurait ajouté cinq écrans devant le seul moment qui
    accroche, sur l'axe qui est le canal d'acquisition de l'autre.
  */
  const [maturite, setMaturite] = useState<Record<string, boolean>>({});

  /*
    Les deux réponses hors échelle — 26/08/2026.

    Elles ne font franchir aucune marche. La première rend le rapport le sien :
    sans elle, le lecteur apprend son palier sans savoir sur quoi il porte. La
    seconde ne mesure rien, elle réveille — c'est la question que personne ne
    pose à un dirigeant de PME.
  */
  const [postesAvecIa, setPostesAvecIa] = useState<number[]>([]);
  const [donneesClients, setDonneesClients] = useState<boolean | null>(null);

  /*
    Le repère du secteur — 25/08/2026.

    Le salarié sait où il se situe ; le dirigeant voyait un montant et rien
    pour le juger. « Quarante heures par semaine, dans mon secteur, c'est
    beaucoup ? » n'avait aucune réponse dans le produit, alors que chaque
    diagnostic dirigeant en apporte un morceau.

    `null` tant que le seuil du secteur n'est pas atteint, et l'écran
    n'affiche alors rien : c'est le contrat du baromètre, pour la même raison
    — un repère pris en défaut une fois ne se consulte plus.
  */
  const [repere, setRepere] = useState<RepereSecteur | null>(null);
  const [description, setDescription] = useState("");

  const [postes, setPostes] = useState<PosteEntreprise[]>([]);
  const [generique, setGenerique] = useState(false);
  const [chargement, setChargement] = useState(false);
  const [echec, setEchec] = useState(false);
  const [heures, setHeures] = useState<Heures>({});
  const [insuffisant, setInsuffisant] = useState(false);
  const [planEtat, setPlanEtat] = useState<
    | { statut: "chargement" }
    | { statut: "ok"; plan: Plan }
    | { statut: "echec"; raison: EchecPlan }
  >({ statut: "chargement" });

  /** Le résultat s'est affiché en estimation basse — barème en jeu. */
  const estimeAuResultat = useRef(false);
  /** L'affinage n'est compté qu'une fois, même si le salaire est retapé. */
  const affineEnvoye = useRef(false);

  useEffect(() => {
    if (etape === "profil") track("dirigeant_demarre", {});
  }, [etape]);

  /**
   * Changer d'étape remonte la page et déplace le focus — 02/09/2026.
   *
   * Cet axe a trois écrans successifs — profil, postes, résultat — et le
   * changement ne faisait rien du tout : ni défilement, ni focus. Le dirigeant
   * validait ses postes en bas de page et atterrissait au milieu de l'écran
   * suivant, sur une section dont il ne voyait ni le titre ni le début.
   *
   * Pour qui navigue au clavier, c'était pire : le curseur restait sur un
   * bouton d'un écran démonté, donc sur le corps du document, et la tabulation
   * suivante repartait du haut de la page sans qu'aucune annonce n'explique
   * pourquoi.
   *
   * Le parcours salarié fait ce geste depuis toujours — c'est le rôle de
   * `zone-vue` dans `App.tsx`. L'axe dirigeant, ajouté après, ne l'a jamais eu :
   * deux parcours, deux structures, une règle appliquée d'un seul côté.
   *
   * La première étape est exclue, comme à l'accueil : on y arrive par le haut
   * sans avoir rien déclenché, et voler le focus à qui vient de charger la page
   * lui reprendrait sa position de lecture.
   */
  const premiereEtape = useRef(true);
  useEffect(() => {
    if (premiereEtape.current) {
      premiereEtape.current = false;
      return;
    }
    remonter();
    // `preventScroll` : le défilement est déjà en cours juste au-dessus, et le
    // laisser au focus l'annulerait par un saut sec — exactement ce que
    // `prefers-reduced-motion` demande d'éviter pour qui ne l'a pas demandé.
    document.getElementById("contenu")?.focus({ preventScroll: true });
  }, [etape]);



  /**
   * L'action offerte se rédige pendant que le résultat s'affiche — même
   * découpage que l'axe salarié : le chiffre d'abord, instantané, puis la
   * preuve, qui prend le temps d'une rédaction. Personne n'attend devant un
   * écran vide.
   */
  async function demanderPlan() {
    if (secteur === "" || postes.length === 0) return;
    setPlanEtat({ statut: "chargement" });
    const declares = postes
      .map((p, i) => ({ name: p.name, hoursPerWeek: heures[i] ?? 0 }))
      .filter((p) => p.hoursPerWeek > 0);
    const reponse = await fetchPlanEntreprise(secteur, description, declares);
    if (reponse.statut === "ok") setPlanEtat({ statut: "ok", plan: reponse.plan });
    else setPlanEtat({ statut: "echec", raison: reponse.raison });
  }

  async function demanderPostes() {
    if (secteur === "" || effectif === null) return;
    setChargement(true);
    setEchec(false);
    const reponse = await fetchPostesEntreprise(secteur, description);
    setChargement(false);
    if (reponse === null) {
      setEchec(true);
      return;
    }
    setPostes(reponse.postes);
    setGenerique(reponse.degraded);
    setHeures({});
    setEtape("postes");
    track("dirigeant_questionnaire", { secteur, generique: reponse.degraded });
  }

  /**
   * Le résultat, recalculé à chaque frappe.
   *
   * Le calcul est pur et instantané : rien ne justifierait un écran d'attente,
   * ni un appel serveur. C'est aussi ce qui permet au montant de se corriger
   * sous les yeux du dirigeant quand il saisit son salaire moyen.
   */
  const resultat: PayrollResult | null = useMemo(() => {
    if (secteur === "" || effectif === null || postes.length === 0) return null;
    const montant = Number(salaire.replace(/[^\d]/g, ""));
    return computePayrollDrain({
      sector: secteur,
      staff: effectif,
      // Le chiffre d'affaires n'est pas demandé : c'est l'arbitrage du 18/08.
      // Sans lui, le moteur retient la tranche la plus basse et sous-estime
      // plutôt que de deviner.
      revenue: null,
      averageSalary: Number.isFinite(montant) && montant > 0 ? montant : null,
      tasks: postes.map((p, i) => ({
        name: p.name,
        auto: p.auto,
        hoursPerWeek: heures[i] ?? 0,
      })),
    });
  }, [secteur, effectif, salaire, postes, heures]);

  /**
   * Le coût horaire à transmettre, ou `null`.
   *
   * Il n'existe que lorsqu'il vient réellement du dirigeant — un coût horaire
   * `estime` ou `estime_bas` est le nôtre, pas le sien : le transmettre
   * reviendrait à lui renvoyer notre propre estimation en la faisant passer
   * pour la sienne, et la case n'aurait plus rien à autoriser.
   */
  const resultatCout: number | null = useMemo(() => {
    if (resultat === null) return null;
    const venuDeLui =
      resultat.hourlyCostSource === "salaire_moyen" ||
      resultat.hourlyCostSource === "masse_salariale" ||
      resultat.hourlyCostSource === "declare";
    return venuDeLui ? resultat.hourlyCost : null;
  }, [resultat]);

  useEffect(() => {
    if (etape !== "resultat" || secteur === "") return;
    let vivant = true;
    void fetchRepereSecteur(secteur).then((valeur) => {
      if (vivant) setRepere(valeur);
    });
    return () => {
      vivant = false;
    };
  }, [etape, secteur]);

  /**
   * Le profil déposé pour la fenêtre de paiement — le rapport à 15 000 F se
   * rédige sur ces postes-là.
   *
   * **Le salaire n'y figure jamais.** Ce qui peut y figurer, et seulement si
   * le dirigeant a coché la case, est le **coût horaire** qui en est dérivé :
   * la seule valeur dont le calcul du rapport a besoin.
   *
   * Sans cette case, l'écran affichait un montant fondé sur ses propres
   * salaires et le rapport payé en portait un autre, plus bas, fondé sur onze
   * coefficients non sourcés. Il avait payé 15 000 F pour moins de précision
   * qu'il n'en avait gratuitement.
   */
  useEffect(() => {
    if (etape !== "resultat" || secteur === "" || effectif === null) return;
    /*
      Les rangs sont réindexés sur les postes retenus — 02/09/2026.

      Le profil ne porte que les postes déclarés à plus de zéro heure, alors que
      la question « une IA sert-elle déjà ici ? » se répond sur le tableau
      complet. Sans cette conversion, le rapport à 15 000 F nommait un autre
      poste que celui désigné. Voir `lib/postesDeclares.ts`.
    */
    const declaration = declarerPostes(postes, heures, postesAvecIa);
    setEntreprise(
      declaration.postes.length === 0
        ? null
        : {
            secteur,
            staff: effectif,
            description: description.trim() === "" ? null : description.trim(),
            postes: declaration.postes,
            coutHoraire: partageCout && resultatCout !== null ? resultatCout : null,
            maturite,
            postesAvecIa: declaration.postesAvecIa,
            donneesClients,
            // Ce qui est montré ici sera exigé du rapport — 06/09/2026.
            titres:
              planEtat.statut === "ok"
                ? [planEtat.plan.action.titre, ...planEtat.plan.verrouillees]
                : null,
          },
    );
  }, [
    planEtat,
    etape,
    secteur,
    effectif,
    description,
    postes,
    heures,
    partageCout,
    resultatCout,
    maturite,
    postesAvecIa,
    donneesClients,
    setEntreprise,
  ]);

  /**
   * L'affinage en direct, compté quand il réussit : le résultat s'était
   * affiché sur le barème, et la saisie du salaire l'a converti en chiffre
   * réel. C'est la moitié « rachat » de la mesure des coefficients — l'autre
   * moitié, `dirigeant_estime`, part à l'affichage du résultat.
   */
  useEffect(() => {
    if (
      etape === "resultat" &&
      estimeAuResultat.current &&
      !affineEnvoye.current &&
      resultat !== null &&
      resultat.hourlyCostSource === "salaire_moyen"
    ) {
      affineEnvoye.current = true;
      track("dirigeant_affine", { secteur });
    }
  }, [etape, resultat, secteur]);

  if (etape === "profil") {
    return (
      <section id="contenu" tabIndex={-1}>
        {/*
          Le héros dirigeant — 25/08/2026.

          Cette page ouvrait directement sur un formulaire : secteur, effectif,
          salaire. Un titre de section, deux phrases, et six champs. Rien qui
          donne à quelqu'un qui n'a jamais entendu parler de nous une raison
          d'accorder trois minutes, et surtout **aucun prix annoncé** — il
          découvrait les 15 000 F après avoir tout rempli.

          C'est le seul écran du produit qui reçoit du trafic acheté : la
          campagne Meta pointe ici, avec `utm_campaign=dirigeant-01`. Payer un
          clic pour l'envoyer sur un formulaire nu est la façon la plus sûre de
          brûler un budget.

          Le titre reprend le rythme de l'accueil — « L'IA ne prendra pas votre
          place » — parce que c'est une même marque et que la parenté doit
          s'entendre. Ce qu'il dit change : un dirigeant ne craint pas pour son
          poste, il paie des heures.

          « Ces heures-là, vous les payez déjà » est vrai au sens strict et sans
          exagération : le diagnostic mesure du temps salarié consacré à des
          tâches automatisables. Ce n'est pas une promesse d'économie — c'est un
          constat sur ce qui est déjà décaissé chaque mois.
        */}
        {/* Le rappel d'un achat en cours n'existait qu'à l'accueil : un dirigeant
            qui rouvrait /dirigeant après paiement ne voyait ni « Récupérer mon
            rapport » ni sa référence, alors que l'écran d'après paiement les lui
            promettait « depuis cet appareil ». Relecture du 06/09/2026. */}
        <RappelAchat />
        <div className="dark">
          <div className="shell hero-dir">
            <div className="kicker">Dirigeants de PME · Afrique francophone</div>
            <h1>
              L'IA ne remplacera pas vos équipes. Elle prendra leurs tâches répétitives —{" "}
              <i>et ces heures-là, vous les payez déjà.</i>
            </h1>
            <p className="lede">
              En trois minutes : les postes répétitifs qui occupent vos équipes, ce qu'ils
              représentent en personnes à temps plein, et ce qu'ils coûtent par mois.
            </p>
            <div className="reassure">
              <span>Gratuit</span>
              <span>Sans inscription</span>
              {/*
              « Rien n'est conservé » n'était pas vrai — 26/08/2026.

              La politique de confidentialité, deux écrans plus bas, dit
              l'inverse en toutes lettres : le résultat agrégé et anonyme
              **est** conservé pour le calcul des comparaisons, et le
              navigateur retient un nombre durable qui fait fonctionner le
              parrainage. Ce qui n'est pas conservé, c'est le texte du CV,
              et ce qui n'est jamais demandé, c'est ce qui vous désigne.

              Un badge qui contredit la politique de confidentialité du même
              site est un faux signal au sens de la règle n° 6 — et il porte
              sur la promesse même qui décide quelqu'un à essayer.
                */}
              <span>Rien qui vous désigne</span>
            </div>
            {/*
              Un départ visible dès l'arrivée — 06/10/2026.

              Sur téléphone, la première question tombait à 848 px pour un
              écran de 727 : le dirigeant arrivé d'une annonce qui promet
              « trois minutes » ne voyait que du texte. Le bouton mène au
              formulaire et y pose le focus ; l'encadré du prix reste avant
              lui, à sa place.
            */}
            <button
              type="button"
              className="btn btn-brass"
              style={{ marginTop: 22 }}
              onClick={() => {
                defilerVers("d-depart");
                document.getElementById("d-secteur")?.focus({ preventScroll: true });
              }}
            >
              Commencer — trois minutes
            </button>
          </div>
        </div>

        <div className="shell narrow app">
        {/*
          Le prix, avant le formulaire et non après.

          Un dirigeant qui remplit six champs puis découvre 15 000 F a été mené
          quelque part sans le savoir. Le dire ici coûte quelques abandons et
          les rend honnêtes — et ceux qui restent savent ce qu'ils viennent
          chercher, ce qui est exactement la population qu'on veut mesurer.
        */}
        <div className="notice" id="d-depart">
          <b>Le diagnostic est gratuit et complet.</b>
          <p className="fine" style={{ margin: "6px 0 0" }}>
            Vous verrez votre montant, vos postes classés par coût, et le début de la première
            action — sa raison d'être, sa première étape en détail et le piège à éviter. Le rapport
            complet — dix actions à confier, cinq automatisations, le plan des trente premiers
            jours — est à {fcfa(prixEntreprise ?? PLANS.entreprise)} F, et vous décidez après
            avoir vu le chiffre.
          </p>
        </div>

        <div className="field">
          <label htmlFor="d-secteur">Votre secteur</label>
          <select
            id="d-secteur"
            value={secteur}
            onChange={(e) => setSecteur(e.target.value as SectorCode)}
          >
            <option value="">Choisissez…</option>
            {SECTORS.map((s) => (
              <option key={s.code} value={s.code}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label id="d-eff-l">Combien de personnes travaillent avec vous ?</label>
          <div className="seg seg-large" role="group" aria-labelledby="d-eff-l">
            {STAFF_BRACKETS.map((libelle, i) => (
              <button
                key={libelle}
                type="button"
                aria-pressed={effectif === i}
                onClick={() => setEffectif(i as StaffBracket)}
              >
                {libelle}
              </button>
            ))}
          </div>
          <p className="fine">Salariés et prestataires réguliers compris.</p>
        </div>

        {/*
          La question d'argent, et elle est facultative.

          Elle porte sur le salaire moyen versé, jamais sur le chiffre
          d'affaires : elle concerne les employés et non les résultats de
          l'entreprise, donc elle est moins intrusive. Et surtout, la donner
          retire onze coefficients sectoriels du calcul — c'est écrit ici, parce
          que c'est un argument honnête pour répondre plutôt qu'une insistance.
        */}
        <div className="field">
          <label htmlFor="d-salaire">
            Salaire mensuel moyen des personnes qui font le travail de bureau et de gestion chez
            vous — facultatif
          </label>
          <p className="fine" style={{ margin: "0 0 6px" }}>
            Pas celui des ouvriers ou des équipes de terrain : les postes mesurés sont ceux du
            bureau.
          </p>
          <input
            id="d-salaire"
            inputMode="numeric"
            placeholder="par exemple 250 000"
            value={salaire}
            onChange={(e) => setSalaire(e.target.value)}
          />
          {/*
            La phrase qui compte est la première, et elle est vraie au sens
            strict : le calcul du montant se fait dans le navigateur, et
            l'appel serveur du plan n'envoie que les postes et les heures.
            C'est la première méfiance d'un dirigeant — « où va ce chiffre ? »
            — et on y répond avant qu'il ne la formule.
          */}
          <p className="fine">
            Ce chiffre ne quitte pas votre appareil : le calcul se fait dans votre navigateur, rien
            n'est envoyé ni conservé. Avec lui, le montant est le vôtre ; sans lui, une moyenne
            prudente de votre secteur, volontairement sous-évaluée.
          </p>
        </div>

        {/*
          Sortie de son pli — elle y était cachée derrière un « facultatif »
          replié, alors que c'est elle qui rend le questionnaire personnel :
          les postes sont nommés avec les mots du dirigeant au lieu de venir
          d'une liste par secteur. Ce qu'on cache n'existe pas.
        */}
        <div className="field">
          <label htmlFor="d-desc">Ce qui vous prend du temps, en une phrase — facultatif</label>
          <input
            id="d-desc"
            placeholder="On ressaisit chaque commande trois fois."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={400}
          />
          <p className="fine">
            Une phrase suffit : vos questions seront écrites avec vos mots, au lieu de venir d'une
            liste générique de votre secteur.
          </p>
        </div>

        {echec && (
          <div className="notice" role="status">
            <b>La connexion s'est interrompue.</b> Votre questionnaire n'a pas pu être préparé —
            rien n'est perdu, vos réponses sont toujours à l'écran.
          </div>
        )}

        <button
          type="button"
          className="btn btn-brass"
          disabled={secteur === "" || effectif === null || chargement}
          onClick={() => void demanderPostes()}
        >
          {chargement ? "Préparation de vos questions…" : "Voir où part le temps"}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onSortir}>
          ← Je suis salarié
        </button>

        {/*
          Les trois objections d'un dirigeant, dans l'ordre où elles viennent.

          Elles ne sont pas décoratives : ce sont les trois questions qu'il pose
          réellement avant de donner trois minutes et, plus tard, 15 000 F.
          « Qu'est-ce que j'obtiens », « où vont mes chiffres », « d'où sortent
          les vôtres ». Chacune reçoit une réponse vérifiable — et la troisième
          nomme la limite au lieu de la taire, parce qu'un dirigeant qui la
          découvre seul ne revient pas.
        */}
        <div className="block" style={{ marginTop: "28px" }}>
          <h3>Ce que vous obtenez sans rien payer</h3>
          <p className="sub">
            Le montant mensuel que représentent les heures automatisables de vos équipes,
            l'équivalent en personnes à temps plein, vos postes classés par coût, et le début de la
            première action — prête à confier.
          </p>
        </div>

        <div className="block">
          <h3>Ce qui reste chez vous</h3>
          <p className="sub">
            Le salaire moyen ne quitte pas votre appareil : le calcul se fait dans votre
            navigateur. Aucun nom d'employé n'est demandé, aucun relevé nominatif n'existe. Ce qui
            part vers nos serveurs se limite aux postes et aux heures que vous déclarez.
          </p>
        </div>

        <div className="block">
          <h3>D'où viennent les chiffres</h3>
          <p className="sub">
            Les niveaux d'exposition sont dérivés de l'indice de l'Organisation internationale du
            Travail, et la méthode est publiée en entier — formule comprise. Le coût horaire, lui,
            vient de votre salaire moyen si vous le donnez ; sinon d'un barème sectoriel que nous
            n'avons pas pu sourcer, et qui est alors volontairement sous-évalué. C'est écrit sur le
            résultat comme dans le rapport.
          </p>
          {/*
            Le lien sort de la phrase — la sonde exige 44 px de cible, et un
            lien pris dans un paragraphe en fait seize. Elle a raison : au
            pouce, un mot souligné au milieu d'une ligne se rate une fois sur
            deux. Sur sa propre ligne, il devient atteignable et se voit
            mieux — ce qui vaut mieux pour la seule preuve que cette page
            avance.
          */}
          <a className="lien-cible" href="/methode">
            Lire la méthode et le barème
          </a>
        </div>
        </div>
      </section>
    );
  }

  if (etape === "postes") {
    const renseignes = Object.values(heures).filter((h) => h > 0).length;
    return (
      <section id="contenu" tabIndex={-1}>
        <div className="shell narrow app">
        <div className="kicker">Votre entreprise</div>
        <h2>Combien de temps par semaine, sur chacun de ces postes ?</h2>
        {/*
          « Ce n'est pas un jugement » rassurait un salarié qui craint pour son
          poste. Un dirigeant ne craint pas d'être jugé par son propre
          diagnostic — il craint de perdre du temps et de répondre faux. C'est
          à ces deux inquiétudes qu'on répond.
        */}
        <p className="sub">
          Le temps de toute l'entreprise, vous compris — une estimation à la louche suffit, vous
          pourrez affiner. Deux réponses ouvrent le résultat ; chaque réponse de plus rend le
          chiffre plus proche du vôtre.
        </p>

        {generique && (
          <div className="notice" role="status">
            <b>Questionnaire générique.</b> Nous n'avons pas pu l'ajuster à votre activité cette
            fois. Les postes ci-dessous sont ceux de votre secteur : le résultat reste juste, il est
            simplement moins ajusté.
          </div>
        )}

        <div role="group" aria-label="Temps passé par poste">
          {postes.map((poste, i) => (
            <div className={`tk${heures[i] !== undefined ? " set" : ""}`} key={poste.name}>
              <div className="top">
                <div className="lbl" id={`dpl${i}`}>
                  {poste.name}
                </div>
              </div>
              <div className="seg" role="group" aria-labelledby={`dpl${i}`}>
                {WEEKLY_HOUR_STEPS.map((pas) => (
                  <button
                    key={pas.label}
                    type="button"
                    aria-pressed={heures[i] === pas.hours}
                    onClick={() => setHeures((h) => ({ ...h, [i]: pas.hours }))}
                  >
                    {pas.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/*
          Le compteur qui monte — le cœur de l'expérience du kit d'origine, et
          ce qui manquait le plus à cet écran : chaque réponse fait bouger le
          chiffre sous les yeux du dirigeant. Ce n'est pas un artifice, c'est le
          vrai calcul affiché en continu — exactement celui que l'écran suivant
          détaillera, mention « estimation basse » comprise quand le salaire n'a
          pas été donné. Un compteur qui montrerait autre chose que le résultat
          final serait un faux signal.
        */}
        {/*
          Le compteur était masqué aux lecteurs d'écran — 02/09/2026.

          `aria-hidden="true"` le retirait entièrement de l'arbre
          d'accessibilité. Or le commentaire ci-dessus le décrit comme « le cœur
          de l'expérience » et « le vrai calcul affiché en continu » : c'est la
          seule chose qui dise au dirigeant que ses réponses font bouger
          quelque chose, et le chiffre autour duquel tout l'écran est construit.

          Quelqu'un qui navigue au lecteur d'écran cochait donc sept postes dans
          le silence complet, sans retour, puis découvrait le montant à l'écran
          suivant. C'est exactement le contraire de ce que cet écran veut faire.

          `role="status"` plutôt qu'un simple affichage lisible : chaque
          changement vient d'un geste délibéré — presser un segment d'heures —
          et l'annonce est la contrepartie de ce geste, celle que l'œil reçoit
          déjà. `polite` attend une pause, donc n'interrompt pas la lecture des
          boutons voisins.
        */}
        {resultat !== null && resultat.weeklyHours > 0 && (
          <div className="cumul" role="status" aria-live="polite">
            <span>
              {resultat.weeklyHours} h par semaine déclarées{" "}
            </span>
            <b>≈ {fcfa(resultat.monthly)} F par mois</b>
            {resultat.hourlyCostSource !== "salaire_moyen" &&
              resultat.hourlyCostSource !== "declare" && <i>estimation basse</i>}
          </div>
        )}

        {insuffisant && (
          <div className="err">
            Renseignez le temps passé sur au moins deux postes : le résultat se calcule sur ces
            déclarations.
          </div>
        )}

        <button
          type="button"
          className="btn btn-brass btn-colle"
          onClick={() => {
            if (renseignes < MIN_POSTES_RENSEIGNES) {
              setInsuffisant(true);
              say("Renseignez le temps passé sur au moins deux postes.");
              return;
            }
            setEtape("resultat");
            say("Votre résultat est affiché.");
            track("dirigeant_resultat", { secteur });
            // Le montant affiché repose-t-il sur le barème non sourcé ? C'est
            // le chiffre qui juge les onze coefficients : s'il reste rare, ils
            // ne portent presque aucun montant réel.
            if (
              resultat !== null &&
              (resultat.hourlyCostSource === "estime" ||
                resultat.hourlyCostSource === "estime_bas")
            ) {
              estimeAuResultat.current = true;
              track("dirigeant_estime", { secteur });
            }
            void demanderPlan();
          }}
        >
          Voir le résultat
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => setEtape("profil")}>
          ← Revenir
        </button>
        </div>
      </section>
    );
  }

  return (
    <ResultatDirigeant
      resultat={resultat}
      postes={postes}
      planEtat={planEtat}
      salaire={salaire}
      onSalaire={setSalaire}
      resultatCout={resultatCout}
      partageCout={partageCout}
      onPartageCout={setPartageCout}
      repere={repere}
      maturite={maturite}
      onMaturite={setMaturite}
      postesAvecIa={postesAvecIa}
      onPostesAvecIa={setPostesAvecIa}
      donneesClients={donneesClients}
      onDonneesClients={setDonneesClients}
      onReessayerPlan={() => void demanderPlan()}
      onRecommencer={() => setEtape("profil")}
    />
  );
}

const fcfa = (n: number) => n.toLocaleString("fr-FR");

/**
 * Le résultat, et l'ordre dans lequel il se lit.
 *
 * **L'équivalent temps plein d'abord** — le seul chiffre qui ne repose sur
 * aucune hypothèse. Puis le montant, avec sa base et son équivalent parlant.
 * Puis le miroir : chaque poste avec ses heures et son coût, pour que le
 * dirigeant se reconnaisse au lieu de lire un reçu. Et enfin la preuve :
 * l'action offerte, entière, avant toute demande d'argent — le même geste que
 * l'axe salarié, qui manquait ici et laissait l'écran demander 15 000 F sans
 * avoir rien donné.
 */
function ResultatDirigeant({
  resultat,
  postes,
  planEtat,
  salaire,
  onSalaire,
  resultatCout,
  partageCout,
  onPartageCout,
  repere,
  maturite,
  onMaturite,
  postesAvecIa,
  onPostesAvecIa,
  donneesClients,
  onDonneesClients,
  onReessayerPlan,
  onRecommencer,
}: {
  resultat: PayrollResult | null;
  postes: PosteEntreprise[];
  planEtat:
    | { statut: "chargement" }
    | { statut: "ok"; plan: Plan }
    | { statut: "echec"; raison: EchecPlan };
  salaire: string;
  onSalaire: (valeur: string) => void;
  /** Le coût horaire venu du dirigeant, ou `null` quand il vient de nous. */
  resultatCout: number | null;
  partageCout: boolean;
  onPartageCout: (valeur: boolean) => void;
  /** Ce que déclarent les autres du secteur, ou `null` sous le seuil. */
  repere: RepereSecteur | null;
  /*
    Les réponses de maturité vivent chez le parent, et non ici.

    C'est lui qui compose le profil transmis au paiement : les garder dans
    l'écran de résultat les perdrait au moindre retour en arrière, et le
    rapport payé n'aurait pas les réponses que l'acheteur venait de donner.
  */
  maturite: Record<string, boolean>;
  onMaturite: (maj: (avant: Record<string, boolean>) => Record<string, boolean>) => void;
  /** Les rangs des postes où une IA sert déjà. Hors échelle : aucun palier. */
  postesAvecIa: number[];
  onPostesAvecIa: (maj: (avant: number[]) => number[]) => void;
  /** `null` tant que la question n'a pas été répondue. */
  donneesClients: boolean | null;
  onDonneesClients: (valeur: boolean) => void;
  onReessayerPlan: () => void;
  onRecommencer: () => void;
}) {
  const [copie, setCopie] = useState<string | null>(null);
  const conditionsMaturite = echelleMaturite("dirigeant").conditions;
  const palier = classer("dirigeant", maturite);
  const toutesRepondues = conditionsMaturite.every((c) => c.cle in maturite);
  if (resultat === null) return null;
  const estime = resultat.hourlyCostSource === "estime" || resultat.hourlyCostSource === "estime_bas";
  // La préposition contractée vient du moteur avec l'image : « d'un mois de
  // salaire », « du salaire annuel chargé » — « de un mois » se lisait ici.
  const image = speakingEquivalentAvecDe(resultat.yearly, resultat.hourlyCost);
  const occupes = resultat.ranked.filter((ligne) => ligne.hoursPerWeek > 0);

  return (
    <section id="contenu" tabIndex={-1}>
      <div className="shell narrow app">
      <div className="kicker">Votre entreprise</div>
      <h2>Ce que les tâches automatisables occupent chez vous</h2>

      <div className="bignum sur-clair" aria-live="polite">
        {resultat.fullTimeEquivalent.toLocaleString("fr-FR")}
        <small> personne{resultat.fullTimeEquivalent >= 2 ? "s" : ""} à temps plein</small>
      </div>
      <p className="sub">
        Vos équipes déclarent {resultat.weeklyHours} heures par semaine sur ces postes, dont{" "}
        {resultat.automatableHours.toLocaleString("fr-FR")} qu'une IA peut prendre en charge. C'est
        de l'arithmétique sur vos réponses : aucune moyenne, aucune estimation.
      </p>

      <div className="block">
        <h3>Ce que ce temps coûte</h3>
        <div className="bignum sur-clair">
          {fcfa(resultat.monthly)}
          <small> F par mois</small>
        </div>
        {/*
          L'image vient APRÈS le montant, jamais à sa place : elle traduit un
          chiffre déjà arrondi à la baisse, elle ne le remplace pas. Et sur un
          petit montant, `speakingEquivalent` se tait plutôt que de forcer une
          comparaison — pas de faux signaux.
        */}
        {image !== null && (
          <p className="sub">
            Sur un an, {fcfa(resultat.yearly)} F — l'équivalent <b>{image}</b>.
          </p>
        )}
        <p className="fine">
          Base de calcul : {fcfa(resultat.hourlyCost)} F de l'heure, chargés.{" "}
          {estime
            ? "Estimé depuis les moyennes de votre secteur, faute de salaire déclaré, et volontairement sous-évalué."
            : resultat.hourlyCostBorne === "plancher"
              ? "Votre salaire moyen donnait moins : nous retenons le plancher du barème, jamais en dessous."
              : resultat.hourlyCostBorne === "plafond"
                ? "Votre salaire moyen donnait plus : nous retenons le plafond du barème."
                : "Déduit du salaire moyen que vous avez indiqué. Ce montant ne dépend d'aucune moyenne sectorielle."}{" "}
          Total arrondi à la baisse. C'est une estimation destinée à la décision, pas une
          comptabilité.
        </p>
        {/*
          L'affinage se fait ICI, sous le chiffre, et en direct : le calcul vit
          dans le navigateur, donc saisir le salaire fait bouger le montant
          sous les yeux. L'ancien texte disait « indiquez votre salaire » sans
          offrir d'endroit où le faire — une invitation vers nulle part. Et
          c'est le meilleur moment pour poser la question d'argent : la valeur
          est déjà à l'écran, il ne s'agit plus de payer pour voir mais
          d'affiner ce qu'on voit.
        */}
        {/*
          Visible tant qu'on estime — et dès qu'il porte une valeur. Sans la
          seconde condition, taper le premier chiffre faisait basculer le
          calcul en « salaire déclaré », donc démontait le champ sous les
          doigts au milieu de la saisie. C'est le script de vérification qui
          l'a vu, en échouant à retrouver le champ après l'avoir rempli.
        */}
        {(estime || salaire !== "") && (
          <div className="field" style={{ marginTop: "14px" }}>
            <label htmlFor="d-salaire-affiner">
              {estime
                ? "Pour un chiffre qui soit le vôtre : votre salaire mensuel moyen"
                : "Votre salaire mensuel moyen"}
            </label>
            <input
              id="d-salaire-affiner"
              inputMode="numeric"
              placeholder="par exemple 250 000"
              value={salaire}
              onChange={(e) => onSalaire(e.target.value)}
            />
            <p className="fine">
              Le montant ci-dessus se recalcule à mesure. Ce chiffre ne quitte pas votre appareil —
              rien n'est envoyé ni conservé.
            </p>

            {/*
              L'écart que cette case répare — 25/08/2026.

              Le montant affiché ici se calcule avec le salaire saisi. Le
              rapport payé, lui, était rédigé depuis les onze coefficients
              sectoriels non sourcés, faute de recevoir quoi que ce soit : un
              dirigeant qui remplissait ce champ voyait un montant assis sur
              ses propres salaires, payait 15 000 F, et recevait un montant
              plus bas assis sur une moyenne de secteur. Le document le disait
              — il n'était donc pas trompé — mais il avait payé pour **moins**
              de précision qu'il n'en avait gratuitement.

              Trois règles tiennent cette case. Elle est **décochée par
              défaut**, et la promesse ci-dessus reste alors vraie mot pour
              mot. Elle ne transmet que le **coût horaire**, seule valeur dont
              le calcul a besoin — sans prétendre que c'est beaucoup moins
              qu'un salaire, ce qui serait un faux signal : elle dit ce qu'elle
              fait. Et le modèle ne la reçoit dans aucun cas : le prompt ne
              transmet que des heures, l'argent est calculé par du code.
            */}
            {resultatCout !== null && (
              <div style={{ marginTop: "12px" }}>
                <label
                  htmlFor="d-partage-cout"
                  style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}
                >
                  <input
                    type="checkbox"
                    id="d-partage-cout"
                    checked={partageCout}
                    onChange={(e) => onPartageCout(e.target.checked)}
                    style={{ width: "auto", marginTop: "3px" }}
                  />
                  <span>Utiliser mon chiffre dans le rapport, si je l'achète</span>
                </label>
                <p className="fine" style={{ margin: "6px 0 0 30px" }}>
                  {partageCout
                    ? `Le rapport chiffrera sur ${fcfa(resultatCout)} F de l'heure, votre coût horaire chargé, au lieu de la moyenne de votre secteur. C'est ce coût horaire qui part — pas votre salaire, mais il s'en déduit, et le rapport ne le montre à personne d'autre que vous.`
                    : "Sans cela, le rapport chiffrera sur la moyenne de votre secteur — plus basse, et fondée sur onze coefficients que nous n'avons pas pu sourcer. Rien ne part tant que cette case n'est pas cochée."}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/*
        Le miroir : tous les postes où du temps a été déclaré, avec leurs
        heures et leur coût. Trois intitulés secs faisaient un reçu ; c'est ici
        que le dirigeant doit reconnaître sa semaine.
      */}
      <div className="block">
        <h3>Où passe ce temps</h3>
        <div className="rangs">
          {/*
            Le libellé court, enfin utilisé — 02/09/2026.

            Le prompt demande au modèle, pour chaque poste, « un libellé court
            en 40 signes au plus, sans verbe, **pour le classement du
            résultat** » — et la banque de repli en écrit un pour chacun de ses
            postes, dans le même style : « relances d'impayés », « devis et
            factures manuels ». Le serveur le valide strictement : un libellé
            court vide ou trop long **rejette le pack entier**, sept bons postes
            avec lui, et la génération se rejoue ou tombe sur la banque.

            Ce classement — celui que le prompt nomme — affichait `name`, un
            nom complet de 15 à 80 signes, sur une ligne qui porte déjà les
            heures et un montant. On payait donc des jetons à chaque
            génération, et l'on risquait de perdre une génération entière, pour
            un champ qu'aucun écran ne lisait.

            La personne a vu le nom complet à l'écran précédent, où elle a
            déclaré ses heures : ici, le libellé court rappelle, il ne présente
            pas. C'est exactement ce pour quoi il a été écrit.

            Le repli sur `name` couvre le cas où le pack aurait changé sous un
            résultat déjà calculé — les noms sont uniques, le serveur le
            vérifie, donc la recherche aboutit dans le cas ordinaire.
          */}
          {occupes.map((ligne, i) => (
            <div className="rang rang-detail" key={ligne.name}>
              <div className="n">{String(i + 1).padStart(2, "0")}</div>
              <div className="t">
                {postes.find((p) => p.name === ligne.name)?.short ?? ligne.name}
              </div>
              <div className="mesure">
                {ligne.hoursPerWeek} h/sem · ≈ {fcfa(Math.round(ligne.monthlyCost / 1000) * 1000)} F/mois
              </div>
            </div>
          ))}
        </div>
        <p className="fine">
          Classés par coût, sur les {postes.length} postes mesurés. Les coûts par poste sont
          arrondis au millier ; le total en haut est arrondi à la baisse.
        </p>

        {/*
          Le repère, quand le secteur en a un.

          Il n'apparaît qu'au-delà du seuil, et il ne juge pas : il situe. Rien
          n'est écrit du genre « vous êtes au-dessus de la moyenne » — l'écart
          est donné, le dirigeant en tire ce qu'il veut. Un produit qui se
          contente de mesurer n'a pas à décerner de bons points, et un repère
          qui commenterait ses propres chiffres cesserait d'être citable.
        */}
        {repere !== null && repere.medianHeures !== undefined && (
          <div className="notice" style={{ marginTop: "16px" }} aria-live="polite">
            <b>Ce que déclarent les autres.</b>
            <p className="fine" style={{ margin: "6px 0 0" }}>
              Sur {repere.effectif} entreprises de votre secteur passées par ce diagnostic, la
              médiane est de {repere.medianHeures} heures par semaine sur ces postes
              {repere.partAutomatisable !== undefined
                ? `, dont ${repere.partAutomatisable} % sur des tâches automatisables`
                : ""}
              . Vous en déclarez {resultat.weeklyHours}. Ce sont des déclarations, pas des
              mesures : elles disent ce que des dirigeants estiment, ce qui reste la seule
              source qui existe sur ce chiffre.
            </p>
          </div>
        )}
      </div>

      <PlanDirigeant
        planEtat={planEtat}
        onReessayer={onReessayerPlan}
        palierRenseigne={palier.renseigne}
      />

      {/*
        Où vous en êtes — 26/08/2026.

        Cinq questions, posées après le résultat parce que c'est là qu'elles
        coûtent le moins : le montant est déjà à l'écran, la personne sait
        pourquoi elle répond. Les poser avant aurait ajouté cinq écrans devant
        le seul moment qui accroche, sur l'axe qui est le canal d'acquisition
        de l'autre.

        Le palier est **donné**, la trajectoire est vendue — même économie que
        l'axe salarié, où l'indice est gratuit et le chemin dans le rapport.
        Retenir le palier ne ferait pas vendre davantage : cela priverait
        l'écran de la seule chose qui donne envie de lire la suite.

        L'échelle vient de `@riseafrica/scoring` et non d'une copie locale : le
        palier affiché ici et celui du rapport payé doivent être le même, et
        deux définitions divergent toujours.
      */}
      <div className="block">
        <h3>Où en est votre entreprise</h3>
        <p className="sub">
          Cinq questions, et vous savez où vous vous situez. Ce n'est pas une note : c'est un
          palier, défini par ce que vous répondez — rien d'autre.
        </p>
        {/*
          Les deux premières questions portent sur ce que font ses salariés, et
          un dirigeant de quarante personnes n'en sait pas toujours rien. Le
          dire coûte une ligne ; le taire produit un palier assis sur une
          supposition, sans que personne ne puisse le savoir.
        */}
        <p className="fine">
          Les deux premières portent sur ce que font vos équipes. Si vous n'êtes pas sûr,
          demandez avant de répondre&nbsp;: un palier assis sur une supposition ne vous sert à
          rien.
        </p>

        <ul className="mat-questions">
          {conditionsMaturite.map((condition) => (
            <li key={condition.cle} className="mat-question">
              <p id={`mat-${condition.cle}`}>{condition.question}</p>
              <div className="mat-choix" role="group" aria-labelledby={`mat-${condition.cle}`}>
                {[true, false].map((valeur) => (
                  <button
                    key={String(valeur)}
                    type="button"
                    className="mat-bouton"
                    aria-pressed={maturite[condition.cle] === valeur}
                    onClick={() => onMaturite((avant) => ({ ...avant, [condition.cle]: valeur }))}
                  >
                    {valeur ? "Oui" : "Non"}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>

        {/*
          Le palier n'apparaît qu'une fois **toutes** les questions répondues.

          L'afficher au fil de l'eau donnerait un palier qui monte à chaque
          « oui » — donc un palier faux entre deux réponses, et une impression
          de jauge là où il n'y a pas de note. Le classement s'arrête à la
          première condition manquante ; une question sans réponse n'est pas
          une condition remplie.
        */}
        {/*
          Les deux questions hors échelle, sous les cinq autres.

          Elles ne font monter aucun palier et n'entrent pas dans le
          classement. Elles sont ici, à la suite, parce que c'est le moment où
          la personne répond déjà — et non dans un écran de plus, qui coûterait
          des abandons sur l'axe qui sert de canal d'acquisition à l'autre.
        */}
        {occupes.length > 0 && (
          <div className="mat-hors">
            <p id="mat-postes">Sur lesquels de ces postes une IA sert-elle déjà&nbsp;?</p>
            <p className="fine">
              Aucune réponse ne change votre palier. Elle sert à ce que le rapport parle de vos
              postes plutôt que de postes en général.
            </p>
            <div className="mat-postes" role="group" aria-labelledby="mat-postes">
              {occupes.map((ligne) => {
                const rang = postes.findIndex((p) => p.name === ligne.name);
                const choisi = postesAvecIa.includes(rang);
                return (
                  <button
                    key={ligne.name}
                    type="button"
                    className="mat-poste"
                    aria-pressed={choisi}
                    onClick={() =>
                      onPostesAvecIa((avant) =>
                        choisi ? avant.filter((n) => n !== rang) : [...avant, rang].sort((a, b) => a - b),
                      )
                    }
                  >
                    {ligne.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mat-hors">
          <p id="mat-donnees">{QUESTION_DONNEES.question}</p>
          <div className="mat-choix" role="group" aria-labelledby="mat-donnees">
            {[true, false].map((valeur) => (
              <button
                key={String(valeur)}
                type="button"
                className="mat-bouton"
                aria-pressed={donneesClients === valeur}
                onClick={() => onDonneesClients(valeur)}
              >
                {valeur ? "Oui" : "Non"}
              </button>
            ))}
          </div>
          {/*
            La conséquence s'affiche à la réponse, et pas seulement dans le
            rapport payé : quelqu'un qui vient de reconnaître l'imprudence doit
            savoir quoi faire tout de suite, pas après un achat.
          */}
          {donneesClients === true && <p className="mat-alerte">{QUESTION_DONNEES.siOui}</p>}
        </div>

        {toutesRepondues && (
          <div className="mat-palier" aria-live="polite">
            <div className="mat-palier-rang">Palier atteint</div>
            <b>{palier.palier.nom}</b>
            <p>{palier.palier.dit}</p>
            {palier.restantes.length > 0 ? (
              <p className="mat-suite">
                <b>La marche suivante :</b> {palier.restantes[0].acquis}{" "}
                {palier.restantes.length > 1
                  ? `Il en reste ${palier.restantes.length} au total — le rapport donne l'ordre, le geste et le mois de chacune.`
                  : "Le rapport donne le geste et le mois."}
              </p>
            ) : (
              <p className="mat-suite">
                {/*
                  Le dernier palier ne dit plus « plus rien à faire ».

                  On peut être parfaitement organisé sur la rédaction d'e-mails
                  pendant que les sept postes déclarés n'ont rien. L'échelle
                  mesure l'organisation, pas l'étendue — et clore le sujet à qui
                  n'a rien confié serait le plus coûteux des faux signaux.
                */}
                {reserveDuDernierPalier(postesAvecIa.length, postes.length) ??
                  "Vous êtes au dernier palier. Le rapport ne vous proposera aucune marche supplémentaire — ce qui vous reste à gagner est dans le tableau des postes."}
              </p>
            )}
          </div>
        )}
      </div>

      {/*
        Le bloc existait avec sa promesse, sans le moyen de la tenir.

        Il disait « vos salariés peuvent faire le leur » et n'offrait qu'un
        bouton « refaire avec d'autres réponses » : rien à transmettre, aucun
        lien, aucun message. Or l'arbitrage du 18/08 dit que **l'axe dirigeant
        est d'abord un canal d'acquisition pour l'axe salarié** — un dirigeant
        convaincu fait passer le diagnostic à ses employés, qui font chacun le
        leur. Le mécanisme sur lequel repose l'économie de l'axe n'existait
        pas ; il était espéré.

        Le lien porte le `sid` du dirigeant, comme tout partage du produit :
        les diagnostics complétés par son équipe comptent donc comme des
        filleuls.

        Ce qu'ils lui ouvrent, en revanche, a été réécrit le 25/08. La première
        version annonçait « votre second diagnostic à 90 jours s'ouvre » — la
        contrepartie inscrite au ticket T15. Elle n'ouvre rien : le diagnostic
        est librement répétable, par tout le monde, aujourd'hui comme dans trois
        mois. Et elle ne pourra jamais rien ouvrir, parce qu'il n'existe aucun
        identifiant à qui opposer une limite : `SID` est un `Math.random()`
        recalculé **à chaque chargement de page**, et le poser durablement dans
        le navigateur coûterait un traceur — c'est-à-dire l'inverse de ce que ce
        produit promet — pour un obstacle qu'une fenêtre privée défait.

        La contrepartie réelle est ailleurs, et elle est spécifique à cet axe :
        `/repere?secteur=` ne publie ce que déclarent les autres entreprises
        qu'au-delà de `COMPARAISON_SEUIL`, et se tait en dessous. Un dirigeant
        qui fait passer le test autour de lui **fait exister son propre point de
        comparaison**. Ce n'est pas une récompense qu'on lui accorde, c'est une
        conséquence mécanique qu'il peut vérifier sur son écran — la seule forme
        de contrepartie que la règle n° 6 autorise.
      */}
      <div className="block">
        <h3>Vos salariés peuvent faire le leur</h3>
        <p className="sub">
          Le diagnostic individuel est gratuit et sans inscription. Chacun voit ce que l'IA change
          à son propre poste — et une équipe qui l'a constaté par elle-même accompagne vos
          changements au lieu de les subir. Vous n'en voyez rien : nous ne conservons aucun relevé
          nominatif, et c'est ce qui fait qu'ils répondent honnêtement.
        </p>
        <div className="share-opts">
          <button
            type="button"
            className="btn btn-wa"
            onClick={() => {
              shareWhatsApp(MESSAGE_EQUIPE);
              track("partage_equipe", { voie: "whatsapp" });
            }}
          >
            Envoyer à votre équipe
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={async () => {
              const ok = await copyTestLink();
              setCopie(
                ok
                  ? "Lien copié — il ne porte aucun de vos chiffres"
                  : "Copie impossible sur ce navigateur",
              );
              track("partage_equipe", { voie: "lien" });
            }}
          >
            Copier le lien
          </button>
        </div>
        {copie !== null && (
          <p className="fine" aria-live="polite" style={{ marginTop: "8px" }}>
            {copie}
          </p>
        )}
        <p className="fine" style={{ marginTop: "10px" }}>
          Le lien ne contient ni vos postes, ni vos heures, ni le moindre montant. Et refaire
          ce diagnostic est gratuit — le vôtre à tout moment, le leur à quatre-vingt-dix jours ou
          dès qu'ils font passer le test à deux personnes — ce que vos envois changent est ailleurs : le repère de votre secteur ne
          s'affiche qu'au-delà d'un certain nombre d'entreprises. En dessous, il se tait. Vous
          n'attendez donc pas une récompense, vous faites exister votre propre point de
          comparaison.
        </p>
        <button
          type="button"
          className="btn btn-contour"
          style={{ marginTop: "14px" }}
          onClick={onRecommencer}
        >
          Refaire avec d'autres réponses
        </button>
      </div>

      {/*
        L'axe dirigeant n'avait aucune boucle de retour, alors que c'est celui
        dont la mesure bouge le plus vite : un salarié qui confie une tâche à
        une IA déplace ses propres heures, un dirigeant déplace celles de toute
        une équipe. Il portait aussi le livrable le plus cher du catalogue et le
        seul argumentaire de re-mesure du produit — « ce qui compte n'est pas le
        montant, c'est l'écart avec la dernière fois » — sans rien pour tenir
        cette promesse.

        La re-mesure se fait **avec les mêmes postes** : c'est la seule façon
        d'obtenir un écart qui veuille dire quelque chose, et l'évènement le dit
        au lieu de le supposer.
      */}
      <RappelAgenda
        titre="Re-mesurer les heures automatisables de mon entreprise"
        mesure={`Mesure précédente : l'équivalent de ${resultat.fullTimeEquivalent.toLocaleString("fr-FR")} personne(s) à temps plein. Reprenez les mêmes postes.`}
        pourquoi="Ce chiffre suit ce que font réellement vos équipes. Dès qu'une tâche répétitive passe à un outil, il baisse — et c'est l'écart avec aujourd'hui qui le prouve, pas le montant lui-même."
        accroche="Cette mesure vaut trois mois"
        chemin="/dirigeant"
        ecran="dirigeant"
      />

      <p className="fine">
        La semaine de référence est de {Math.round(HOURS_PER_WEEK)} heures. La méthode complète est
        publiée sur ce site.
      </p>
      </div>
    </section>
  );
}

/**
 * Le message que le dirigeant envoie à son équipe.
 *
 * Il est écrit **de sa main à lui**, pas de la nôtre : c'est lui qui l'envoie,
 * et un texte qui sonnerait comme une publicité de l'éditeur se ferait
 * réécrire ou pas envoyer du tout. D'où le tutoiement collégial, la brièveté,
 * et l'absence de tout superlatif.
 *
 * Deux choses y sont dites parce qu'elles lèvent la vraie objection d'un
 * salarié à qui son patron envoie un lien : **son patron ne verra pas son
 * résultat**, et rien ne lui est demandé. Sans elles, le lien se lit comme une
 * évaluation déguisée, et personne ne répond honnêtement — ce qui rendrait la
 * mesure inutile en plus d'être déloyale.
 *
 * `shareWhatsApp` y ajoute le lien de parrainage, qui porte le `sid` de celui
 * qui partage.
 */
const MESSAGE_EQUIPE = MESSAGE_EQUIPE_PARTAGE;

/** Ce qu'on dit selon la cause. Jamais « une erreur est survenue ». */
const EXPLICATION_PLAN: Record<EchecPlan, string> = {
  trop_de_demandes:
    "Trop de demandes depuis cette connexion sur la dernière heure. Réessayez dans quelques minutes : votre résultat, lui, reste affiché.",
  budget:
    "Notre plafond de dépense du jour est atteint. C'est une limite que nous nous imposons pour ne pas laisser filer les coûts ; elle se lève demain.",
  modele:
    "La rédaction n'a pas abouti cette fois. Nous préférons ne rien afficher plutôt qu'une action générique qui ne vaudrait pas la vôtre.",
  reseau: "La connexion s'est interrompue avant que la réponse n'arrive.",
  inattendu: "Nous n'avons pas pu rédiger votre première action.",
};

/**
 * La preuve avant la demande : une action complète sur le poste le plus lourd,
 * puis les neuf titres verrouillés, puis seulement le prix. Le même
 * renversement que l'axe salarié — on ne demande pas de croire, on montre.
 */
function PlanDirigeant({
  planEtat,
  onReessayer,
  palierRenseigne,
}: {
  planEtat:
    | { statut: "chargement" }
    | { statut: "ok"; plan: Plan }
    | { statut: "echec"; raison: EchecPlan };
  onReessayer: () => void;
  /** Au moins une question de maturité répondue : le rapport aura son palier. */
  palierRenseigne: boolean;
}) {
  // Le prix affiché est celui que le serveur déclare, parce que c'est lui qui
  // sait ce que Chariow facture. Tant qu'il n'a pas répondu, celui du module
  // partagé — la bonne valeur dans le cas ordinaire.
  const { prixEntreprise, say } = useApp();
  const specimenEntreprise = useSpecimen("entreprise");

  /**
   * L'arrivée du plan s'annonce — 02/09/2026.
   *
   * Le départ l'était : « Nous rédigeons la première… une trentaine de
   * secondes », dans une région `aria-live`. L'arrivée, trente secondes plus
   * tard, ne l'était pas. Quelqu'un qui navigue au lecteur d'écran s'entendait
   * dire d'attendre, puis plus rien — et n'avait aucun moyen de savoir quand
   * revenir, sinon relire la page au hasard.
   *
   * L'annonce passe par la région globale plutôt que par un `role="status"`
   * local : c'est le mécanisme du produit, et il fonctionne désormais aussi
   * hors parcours — la région n'était montée que dans le parcours salarié,
   * corrigé le même jour.
   */
  const statutPlan = planEtat.statut;
  useEffect(() => {
    if (statutPlan === "ok") say("Votre première action est prête.");
  }, [statutPlan, say]);

  return (
    <div className="block">
      <h2>Le plan de votre entreprise, en dix actions</h2>

      {planEtat.statut === "chargement" && (
        <>
          <p className="sub" aria-live="polite">
            Nous rédigeons la première, sur votre poste le plus lourd. Une trentaine de secondes —
            votre résultat reste affiché au-dessus pendant ce temps.
          </p>
          <div className="attente-plan" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <i key={i} style={{ width: `${[92, 78, 88, 61, 70][i]}%` }} />
            ))}
          </div>
        </>
      )}

      {planEtat.statut === "echec" && (
        <>
          <div className="notice" role="status">
            <b>La première action n'a pas pu être rédigée.</b>{" "}
            {EXPLICATION_PLAN[planEtat.raison]}
          </div>
          <button type="button" className="btn btn-ghost" onClick={onReessayer}>
            Réessayer
          </button>
        </>
      )}

      {planEtat.statut === "ok" && (
        <>
          <p className="sub">
            {/*
              « Le poste où votre entreprise dépense le plus » — 06/10/2026.
              Le serveur choisit le poste qui prend le plus d'heures
              (`promptPlanEntreprise`), alors que le tableau du dessus classe
              par coût. Les deux ordres divergent dès que l'exposition diffère,
              et l'écran affirmait l'un en montrant l'autre.
            */}
            Voici le début de la première — sur le poste qui prend le plus d'heures à votre
            entreprise. Le rapport l'achève et détaille les neuf autres.
          </p>
          <ActionOfferte action={planEtat.plan.action} />
          <TitresVerrouilles titres={planEtat.plan.verrouillees} />
        </>
      )}

      <div className="verrous-pied">
        {/*
          Un dirigeant ne fera pas ces actions lui-même, et le savait avant
          nous : l'argument n'est pas « vous saurez faire » mais « vous pourrez
          confier, et vérifier sans y passer votre journée ». C'est aussi ce que
          le prompt du rapport demande déjà — dire ce qu'on confie à un employé
          et ce que le dirigeant contrôle.
        */}
        <p>
          Le rapport contient les dix actions complètes, écrites pour être confiées telles quelles
          à vos employés — le pourquoi, chaque étape, le prompt à copier, le piège, et ce que vous
          vérifiez sans y passer votre journée — cinq automatisations qui tournent sans vous,{" "}
          {palierRenseigne ? "votre palier, " : ""}et le plan des trente premiers jours.
        </p>
        {/*
          Le palier n'est promis que s'il sera écrit — 24/09/2026.

          La phrase le promettait toujours, et le bouton d'achat est placé
          **avant** les questions de maturité : un dirigeant pouvait payer sans
          y avoir répondu, et le rapport, qui n'écrit pas un palier sans
          réponses, ne portait alors pas ce qu'on lui avait annoncé.
        */}
        {!palierRenseigne && (
          <p className="fine">
            Le palier de votre entreprise s'y ajoute si vous répondez, plus bas, aux questions sur
            l'usage de l'IA dans votre entreprise.
          </p>
        )}
        {/*
          L'exemple, avant le bouton — 25/08/2026.

          Il n'y en avait aucun ici. Le site proposait deux produits à deux prix
          et n'en montrait qu'un : le seul lien vers un rapport d'exemple vivait
          dans le bloc d'offre salarié, et cet écran-ci appelait `BoutonAchat`
          directement. Un dirigeant décidait donc de **15 000 F** — cinq fois le
          prix de l'autre — sans avoir pu lire une seule page de ce qu'il
          achetait.

          Placé **au-dessus** du bouton, et non en dessous : après le bouton, il
          n'est plus une preuve, il est un regret. La règle du bouton mort vaut
          ici comme ailleurs — s'il n'y a pas d'exemple d'entreprise publié, il
          n'y a pas de lien, et surtout pas un lien vers celui d'un salarié.
        */}
        {specimenEntreprise && (
          <>
            <a
              className="btn btn-ghost"
              href="/specimen?axe=entreprise"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "block", textAlign: "center", marginBottom: "12px" }}
            >
              Lire un rapport d'entreprise complet, en exemple
            </a>
            <p className="fine" style={{ textAlign: "center", marginTop: "-4px" }}>
              Un rapport entier, sur une entreprise fictive. Rien à donner.
            </p>
          </>
        )}
        <BoutonAchat
          plan="entreprise"
          libelle={`Obtenir le rapport de mon entreprise — ${fcfa(prixEntreprise ?? PLANS.entreprise)} F`}
        />
        <p className="fine" style={{ textAlign: "center", marginTop: "10px" }}>
          Remboursé sur simple demande, sans justification, dans les quatorze jours.
        </p>
        <Rassurance />
        <ApresPaiement />
      </div>
    </div>
  );
}
